import React from 'react'
import axios from 'axios'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, type Variants } from 'framer-motion'
import { Envelope, Lock, ArrowRight, ShieldCheck, Eye, EyeSlash } from '@phosphor-icons/react'
import { DotLoader } from '@/components/ui/DotLoader'
import {
  authInputClass as inputClass,
  authInputStyle as inputStyle,
  errorBorderClass,
} from '@/lib/styleKit'
import { useAuthStore } from '@/stores/authStore'
import { setRememberMe } from '@/lib/tokenStorage'
import SocialAuthButtons from './SocialAuthButtons'

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  // Client-only. Decides whether the JWTs land in localStorage (survives a
  // browser restart) or sessionStorage (dies with the tab) — see
  // lib/tokenStorage. Stripped in onSubmit; never sent to the backend.
  remember: z.boolean().optional(),
})

type LoginFormData = z.infer<typeof loginSchema>

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: {
    x: [0, -6, 6, -4, 4, 0],
    transition: { duration: 0.4, ease: 'easeInOut' },
  },
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading } = useAuthStore()
  const [showPassword, setShowPassword] = React.useState(false)
  // Single shared lock so picking a credential fills both fields.
  // Per-field locks prevented the unfocused field from accepting autofill.
  const [formReadOnly, setFormReadOnly] = React.useState(true)
  const unlockForm = () => setFormReadOnly(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    // Checked by default so the historical behaviour (session survives a
    // browser restart) doesn't silently change for existing users.
    defaultValues: { remember: true },
  })

  const onSubmit = async (data: LoginFormData) => {
    // `remember` is a client-side storage choice, not a credential — strip it
    // so the POST body stays exactly { email, password }.
    const { remember, ...credentials } = data
    // Must run BEFORE login(): the store writes the response's tokens
    // synchronously, and this flag is what picks the store they go into.
    setRememberMe(remember ?? true)
    try {
      await login(credentials)
      const from = (location.state as any)?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        navigate('/verify-otp', { state: { email: data.email } })
        return
      }
      // Other errors handled by store/toast
    }
  }

  // Static placeholder — no floating animation. Placeholder visible when empty, hidden on type.
  return (
    <motion.div
      className="w-full"
      variants={formStageVariants}
      initial="hidden"
      animate="show"
    >
      {/* Override Chrome / Safari autofill preview styles — theme-aware */}
      <style>{`
        @keyframes onAutoFillStart { from {} to {} }
        .login-input:-webkit-autofill {
          animation-name: onAutoFillStart;
          animation-duration: 1ms;
        }
        /* Light defaults */
        .login-input:-webkit-autofill,
        .login-input:-webkit-autofill:hover,
        .login-input:-webkit-autofill:focus,
        .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
          -webkit-text-fill-color: #0f1011 !important;
          caret-color: #0f1011 !important;
          border-color: rgba(15,16,17,0.08) !important;
          transition: background-color 9999s ease-in-out 0s, color 9999s ease-in-out 0s;
        }
        /* Dark overrides */
        .dark .login-input:-webkit-autofill,
        .dark .login-input:-webkit-autofill:hover,
        .dark .login-input:-webkit-autofill:focus,
        .dark .login-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #141516 inset !important;
          -webkit-text-fill-color: #f7f8f8 !important;
          caret-color: #f7f8f8 !important;
          border-color: rgba(255,255,255,0.08) !important;
        }
        .login-input:-webkit-autofill:focus {
          border-color: rgba(113,112,255,0.55) !important;
        }

        /* Kill the global :focus-visible ring (defined in index.css) on login inputs.
           We rely on a clean 1px indigo border instead. */
        .login-input:focus,
        .login-input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
          --tw-ring-shadow: 0 0 #0000 !important;
          --tw-ring-offset-shadow: 0 0 #0000 !important;
        }
      `}</style>
      {/* Header */}
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Sign in to your <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">account</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{
            fontSize: '15px',
            lineHeight: 1.5,
            letterSpacing: '-0.165px',
            fontWeight: 400,
          }}
        >
          Continue to your Beevelope dashboard
        </p>
      </motion.div>

      {/* Login Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email — floating label + shake on error */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.email ? 'error' : 'idle'}
          >
            <Envelope
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email address"
              aria-label="Email address"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              readOnly={formReadOnly}
              style={inputStyle}
              className={`${inputClass} ${errors.email ? errorBorderClass : ''}`}
              {...register('email')}
              onFocus={unlockForm}
              onAnimationStart={(e) => {
                if ((e.nativeEvent as AnimationEvent).animationName === 'onAutoFillStart') {
                  unlockForm()
                }
              }}
            />
          </motion.div>
          {errors.email && (
            <motion.p
              id="email-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.email.message}
            </motion.p>
          )}
        </motion.div>

        {/* Password — floating label + shake on error */}
        <motion.div variants={formItemVariants}>
          <motion.div
            className="relative group"
            variants={shakeVariants}
            animate={errors.password ? 'error' : 'idle'}
          >
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
              weight="regular"
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Password"
              aria-label="Password"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              readOnly={formReadOnly}
              style={inputStyle}
              className={`${inputClass} pr-10 ${errors.password ? errorBorderClass : ''}`}
              {...register('password')}
              onFocus={unlockForm}
              onAnimationStart={(e) => {
                if ((e.nativeEvent as AnimationEvent).animationName === 'onAutoFillStart') {
                  unlockForm()
                }
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7a7d83] hover:text-[#0f1011] dark:text-[#62666d] dark:hover:text-[#d0d6e0] transition-colors focus:outline-none z-10"
            >
              {showPassword ? <EyeSlash className="h-4 w-4" weight="regular" /> : <Eye className="h-4 w-4" weight="regular" />}
            </button>
          </motion.div>
          {errors.password && (
            <motion.p
              id="password-error"
              role="alert"
              className="mt-1.5 text-red-600 dark:text-red-400"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: easeOutExpo }}
            >
              {errors.password.message}
            </motion.p>
          )}

          <div className="flex items-center justify-between mt-3">
            <label htmlFor="remember" className="flex items-center gap-2 cursor-pointer group">
              <input
                id="remember"
                type="checkbox"
                className="h-3.5 w-3.5 rounded-sm bg-white border border-black/[0.12] dark:bg-white/[0.02] dark:border-white/[0.12] accent-[#5e6ad2] focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/30 focus:ring-offset-0 cursor-pointer"
                {...register('remember')}
              />
              <span
                className="text-[#5f6166] group-hover:text-[#0f1011] dark:text-[#8a8f98] dark:group-hover:text-[#d0d6e0] transition-colors"
                style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
              >
                Remember me
              </span>
            </label>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#7170ff] dark:hover:text-[#828fff] transition-colors"
              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            >
              Forgot password?
            </button>
          </div>
        </motion.div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
          variants={formItemVariants}
          whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
          whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
        >
          {isLoading ? (
            <>
              {/* §55: the button already announces via aria-busy — a second
                  live region here would double-speak. */}
              <DotLoader size="sm" announce={false} />
              <span>Signing in</span>
            </>
          ) : (
            <motion.span
              className="inline-flex items-center gap-2"
              whileHover="hover"
            >
              Sign in
              <motion.span
                variants={{ hover: { x: 3 } }}
                transition={{ duration: 0.2, ease: easeOutExpo }}
                className="inline-flex"
              >
                <ArrowRight className="h-4 w-4" weight="bold" />
              </motion.span>
            </motion.span>
          )}
        </motion.button>
      </form>

      {/* Security note — amber accent breaks all-indigo monoculture.
          Text colour lives in className, not the inline style: #a3741f was
          ~4.14:1 on the white card (below the 4.5:1 AA floor, at 10px) and had
          no dark partner at all, so dark mode got the same muted brown on
          near-black. amber-700 / amber-300 is the project's semantic amber pair
          (§31, styleKit warn tone) — 5.02:1 on #ffffff, 13.2:1 on the #0f1011
          dark card. The ShieldCheck glyph keeps the reserved #f5a524. */}
      <motion.div
        className="mt-6 flex items-center justify-center gap-1.5 text-amber-700 dark:text-amber-300"
        style={{
          fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
          fontSize: '10px',
          fontWeight: 400,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
        variants={formItemVariants}
      >
        <ShieldCheck className="h-3 w-3" weight="fill" style={{ color: '#f5a524' }} />
        <span>256-bit SSL encrypted</span>
      </motion.div>

      {/* Social / enterprise SSO — hidden unless VITE_WORKOS_AUTH_ENABLED=true */}
      <div className="mt-6">
        <SocialAuthButtons
          redirect={(location.state as any)?.from?.pathname || '/dashboard'}
        />
      </div>

      {/* Divider — flex layout, no bg cutout */}
      <motion.div
        className="flex items-center gap-3 mt-7 mb-6"
        variants={formItemVariants}
      >
        <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
        <span
          className="text-[#7a7d83] dark:text-[#62666d] shrink-0"
          style={{
            fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
            fontSize: '10px',
            fontWeight: 400,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
        >
          New to Beevelope
        </span>
        <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
      </motion.div>

      <motion.button
        type="button"
        onClick={() => navigate('/register')}
        className="w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] hover:bg-[#f5f5f5] hover:border-black/[0.14] dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12] inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011] transition-colors duration-150"
        style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
        variants={formItemVariants}
        whileTap={{ scale: 0.98 }}
      >
        Create an account
      </motion.button>
    </motion.div>
  )
}

export default LoginPage
